// Phone health on the device (2026-10-09; engine/src/health.ts): every uncaught error, unhandled
// rejection and crash an error screen caught is kept in a small deduplicated log (localStorage:
// written at once, even while the page is going down), and goes to the server with the next sync
// as one queued write (the outbox: offline-safe, Idempotency-Key) together with a heartbeat at most
// every 30 minutes: storage kept or not, space used, what waits in the outbox, build, iOS version.
// Nothing the person wrote is in it: messages and frames are scrubbed (engine scrubMessage), the
// route keeps only path words. Kind 'measure-health': a refusal is not listed for the person to redo
// (lib/rejected.ts withoutInlineNotice). Shown to the admin in Ylläpito (health/HealthAdmin.tsx).
import {
  HEALTH_LIMITS,
  eventHash,
  iosVersion,
  scrubMessage,
  scrubRoute,
  topFrames,
  type ClientEvent,
  type ClientEventKind,
  type HealthReport,
  type Heartbeat,
} from '@valyrio/engine/health';
import type { OutboxWrite, ValyrioDB } from '../lib/db';
import { enqueueWrite, uid } from '../lib/sync';
import { buildId } from '../shell/build-id';
// Ääntämisvalmentaja: the coach's device diagnostics go with the report (coach/diagnostics.ts; light)
import { clearCoachDiagnostics, takeCoachDiagnostics } from '../coach/diagnostics';

export const HEALTH_KIND = 'measure-health';
export const HEALTH_PATH = '/api/health/report';
const LOG_KEY = 'valyrio:health';
const DEVICE_KEY = 'valyrio:device';

/** The device's log: events by hash (count = occurrences not yet queued) and when a heartbeat last went. */
interface HealthLog {
  events: Record<string, ClientEvent>;
  heartbeatAt: number;
}

function readLog(): HealthLog {
  try {
    const j = JSON.parse(localStorage.getItem(LOG_KEY) ?? 'null') as HealthLog | null;
    if (j && typeof j === 'object' && j.events && typeof j.events === 'object') return { events: j.events, heartbeatAt: Number(j.heartbeatAt) || 0 };
  } catch {
    /* no storage, or a broken value: start again */
  }
  return { events: {}, heartbeatAt: 0 };
}

function writeLog(log: HealthLog): void {
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(log));
  } catch {
    /* full or blocked: the log is best effort */
  }
}

/** This device's own random id (no fingerprint): kept in localStorage, else one per page. */
let pageDevice: string | null = null;
export function deviceId(): string {
  try {
    const have = localStorage.getItem(DEVICE_KEY);
    if (have && /^[A-Za-z0-9-]{8,40}$/.test(have)) return have;
    const id = uid()
      .replace(/[^A-Za-z0-9-]/g, '')
      .slice(0, 40)
      .padEnd(8, '0');
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return (pageDevice ??= uid()
      .replace(/[^A-Za-z0-9-]/g, '')
      .slice(0, 40)
      .padEnd(8, '0'));
  }
}

const ua = (): string => (typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, HEALTH_LIMITS.ua) : '');
const route = (): string => (typeof location !== 'undefined' ? scrubRoute(location.pathname) : '/');

/** The text and stack of anything thrown. */
export function describeThrown(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error) return { message: `${err.name}: ${err.message}`, ...(err.stack ? { stack: err.stack } : {}) };
  if (typeof err === 'string') return { message: err };
  try {
    return { message: JSON.stringify(err) ?? String(err) };
  } catch {
    return { message: String(err) };
  }
}

/** Browser noise that is not an error of the app. */
const IGNORED = /^ResizeObserver loop|^Error: ResizeObserver loop/;

let perPage = 0;

/** Keep one occurrence in the device's log. Never throws; returns the event (or null when not kept). */
export function recordError(kind: ClientEventKind, err: unknown, now: number = Date.now()): ClientEvent | null {
  try {
    if (perPage >= HEALTH_LIMITS.perPage) return null;
    const d = describeThrown(err);
    if (IGNORED.test(d.message)) return null;
    perPage++;
    const message = scrubMessage(d.message);
    const frames = topFrames(d.stack);
    const hash = eventHash(kind, message, frames);
    const at = new Date(now).toISOString();
    const log = readLog();
    const prev = log.events[hash];
    const e: ClientEvent = {
      hash,
      kind,
      message,
      frames,
      count: Math.min((prev?.count ?? 0) + 1, HEALTH_LIMITS.maxCount),
      firstSeen: prev?.firstSeen ?? at,
      lastSeen: at,
      build: buildId(),
      route: route(),
      ua: ua(),
    };
    log.events[hash] = e;
    const keys = Object.keys(log.events);
    if (keys.length > HEALTH_LIMITS.localEvents) {
      keys
        .sort((a, b) => log.events[a]!.lastSeen.localeCompare(log.events[b]!.lastSeen))
        .slice(0, keys.length - HEALTH_LIMITS.localEvents)
        .forEach((k) => delete log.events[k]);
    }
    writeLog(log);
    return e;
  } catch {
    return null;
  }
}

let installed: (() => void) | null = null;

/** window.onerror and unhandledrejection into the log (once per page). Returns the uninstall. */
export function installHealthReporting(): () => void {
  if (installed) return installed;
  const onError = (e: ErrorEvent) => {
    if (e.error !== undefined && e.error !== null) recordError('error', e.error);
    else if (e.message) recordError('error', e.message);
  };
  const onRejection = (e: PromiseRejectionEvent) => recordError('rejection', e.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  installed = () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    installed = null;
  };
  return installed;
}

/** What a heartbeat reads from the browser (tests give their own). */
export interface HealthEnv {
  storage?: { persisted?: () => Promise<boolean>; estimate?: () => Promise<{ usage?: number; quota?: number }> };
  ua: string;
  standalone: boolean | null;
  build: string;
}

export function browserEnv(): HealthEnv {
  let standalone: boolean | null = null;
  try {
    standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  } catch {
    /* unknown */
  }
  return { storage: typeof navigator !== 'undefined' ? navigator.storage : undefined, ua: ua(), standalone, build: buildId() };
}

async function safely<T>(f: (() => Promise<T>) | undefined): Promise<T | null> {
  try {
    return f ? await f() : null;
  } catch {
    return null;
  }
}

/** This device's heartbeat: storage, outbox size and the age of its oldest item. */
export async function buildHeartbeat(db: ValyrioDB, writes: OutboxWrite[], now: number, env: HealthEnv): Promise<Heartbeat> {
  const [reviews, lessons, persisted, estimate] = await Promise.all([
    db.getAll('reviewOutbox', undefined, 2000),
    db.count('lessonOutbox'),
    safely(env.storage?.persisted?.bind(env.storage)),
    safely(env.storage?.estimate?.bind(env.storage)),
  ]);
  const times = [...writes.map((w) => Date.parse(w.createdAt)), ...reviews.map((r) => Date.parse(r.reviewedAt))].filter(Number.isFinite);
  const oldest = times.length ? Math.min(...times) : null;
  return {
    device: deviceId(),
    build: env.build,
    persisted: typeof persisted === 'boolean' ? persisted : null,
    usage: typeof estimate?.usage === 'number' ? Math.round(estimate.usage) : null,
    quota: typeof estimate?.quota === 'number' ? Math.round(estimate.quota) : null,
    outbox: writes.length + reviews.length + lessons,
    outboxOldestMs: oldest === null ? null : Math.max(0, now - oldest),
    ios: iosVersion(env.ua),
    standalone: env.standalone,
    ua: env.ua,
    at: new Date(now).toISOString(),
  };
}

/**
 * Called by the sync right before it sends the outbox (lib/sync.ts beforeFlush): when errors wait
 * or a heartbeat is due, queue one report, so it goes in this same sync. Never two reports in the
 * outbox at once; what was queued leaves the device's log. Returns true when a report was queued.
 */
export async function queueHealthReport(db: ValyrioDB, now: number = Date.now(), env: HealthEnv = browserEnv()): Promise<boolean> {
  const log = readLog();
  const events = Object.values(log.events)
    .filter((e) => e.count > 0)
    .sort((a, b) => b.lastSeen.localeCompare(a.lastSeen))
    .slice(0, HEALTH_LIMITS.eventsPerReport);
  const beatDue = now - log.heartbeatAt >= HEALTH_LIMITS.heartbeatEveryMs;
  const coach = takeCoachDiagnostics(deviceId());
  if (!events.length && !beatDue && !coach) return false;
  const writes = await db.getAll('writeOutbox');
  if (writes.some((w) => w.kind === HEALTH_KIND)) return false;
  const heartbeat = beatDue ? await buildHeartbeat(db, writes, now, env) : undefined;
  const body: HealthReport = { events, ...(heartbeat ? { heartbeat } : {}), ...(coach ? { coach: coach as NonNullable<HealthReport['coach']> } : {}) };
  await enqueueWrite(db, { method: 'POST', path: HEALTH_PATH, body, kind: HEALTH_KIND });
  // read again: an error recorded while this ran stays for the next report
  const after = readLog();
  for (const e of events) {
    const cur = after.events[e.hash];
    if (!cur) continue;
    if (cur.count <= e.count) delete after.events[e.hash];
    else after.events[e.hash] = { ...cur, count: cur.count - e.count, firstSeen: cur.lastSeen };
  }
  if (heartbeat) after.heartbeatAt = now;
  writeLog(after);
  if (coach) clearCoachDiagnostics(coach);
  return true;
}

/** Tests: forget the page's counters and the device's log. */
export function __resetHealthForTests(): void {
  perPage = 0;
  pageDevice = null;
  installed?.();
  try {
    localStorage.removeItem(LOG_KEY);
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    /* none */
  }
}

/** Tests and the admin panel: what waits on this device. */
export function pendingHealthEvents(): ClientEvent[] {
  return Object.values(readLog().events);
}
